import { Controller, useFormContext } from "react-hook-form";
import { Student } from "../../providers/studentsProvider";
import { StudentSelect } from "../../inputs/StudentSelect";
import { CreateExamFormValues } from "./CreateExamForm";
import { CreateDrivingLessonFormValues } from "./CreateDrivingLessonForm";
import { CreateSimulatorDrivingLessonFormValues } from "./CreateSimulatorDrivingLessonForm";
import { DateTime } from "luxon";
import { useMemo } from "react";

export const StudentInputField = (props: {
  drivingSchoolId?: string;
  instructorId?: string;
  filterStudents?: (student: Student) => boolean;
  size?: "small" | "medium";
  disabled?: boolean;
}) => {
  const { control, setValue, watch } = useFormContext<
    CreateDrivingLessonFormValues | CreateSimulatorDrivingLessonFormValues | CreateExamFormValues
  >();
  const student = watch("student");
  const dateTime = watch("dateTime");
  const drivingLessonType = watch("drivingLessonType");
  const { filterStudents: filterStudents_ } = props;
  const isInThePast = dateTime && dateTime.isValid && dateTime < DateTime.now();
  const filterStudents: (student: Student) => boolean = useMemo(
    () => (student: Student) => {
      if (filterStudents_ && !filterStudents_(student)) {
        return false;
      }
      if (isInThePast) {
        // When an event in the past is added, we allow to select inactive students too,
        // if their last lesson is not longer than 28 days ago ...
        if (student.status === "active") {
          return true;
        }
        let lastLessonAt = student.kpis.mostRecentDrivingLessonDate;
        if (student.kpis.mostRecentTheoryLessonDate) {
          if (!lastLessonAt || student.kpis.mostRecentTheoryLessonDate > lastLessonAt) {
            lastLessonAt = student.kpis.mostRecentTheoryLessonDate;
          }
        }
        return !!(lastLessonAt?.isValid && lastLessonAt >= DateTime.now().minus({ days: 28 }));
      } else {
        // We only allow to select active students for future events ...
        return student.status === "active";
      }
    },
    [filterStudents_, isInThePast],
  );
  return (
    <Controller
      name="student"
      control={control}
      render={({ field, formState }) => (
        <StudentSelect
          inputRef={field.ref}
          label="Fahrschüler"
          size={props.size ?? "medium"}
          disabled={props.disabled || formState.isSubmitting}
          drivingSchoolId={props.drivingSchoolId}
          instructorId={props.instructorId}
          filterStudents={filterStudents}
          value={student}
          onChange={(value) => {
            field.onChange({ target: { value } });
            if (value) {
              const { activeOrMostRecentBookedTraining, bookedTrainings } = value;
              setValue(
                "drivingLicenseClass",
                (() => {
                  if (
                    drivingLessonType !== "simulator" ||
                    activeOrMostRecentBookedTraining.includesSimulatorDrivingLessons
                  ) {
                    return activeOrMostRecentBookedTraining.drivingLicenseClass;
                  }
                  const relevantBookedTrainings = bookedTrainings.filter(
                    (it) => it.includesSimulatorDrivingLessons && !it.isFinished,
                  );
                  return relevantBookedTrainings.length > 0 ? relevantBookedTrainings[0].drivingLicenseClass : "";
                })(),
              );
            }
          }}
          onBlur={field.onBlur}
          error={formState.errors.student?.message}
        />
      )}
      rules={{
        validate: (value: Student | null | undefined) => {
          return value ? true : "Bitte wähle den Fahrschüler aus.";
        },
      }}
    />
  );
};
